/** Page number the API starts counting from. */
const FIRST_PAGE = 1;

/** Shape every paginated list endpoint returns (see `paginteListApiResponseSchema`). */
export type PaginatedListResponse<TItem> = {
  page: number;
  per_page: number;
  page_count: number;
  data: TItem[];
};

/**
 * Fetches every page of a paginated list endpoint and returns them as a single
 * response.
 *
 * Lists that filter, count and split their rows client-side need the whole
 * dataset: asking for one large page silently drops everything past it, which
 * is how the substitutes list ended up showing 100 of ~600 rows. The page size
 * passed by the caller stays the size of each request; `page_count` from the
 * first response drives how many more are needed.
 *
 * @param fetchPage - fetches a single page, 1-indexed
 * @returns every item, in API order, as one page
 */
export const fetchAllPages = async <TItem>(
  fetchPage: (page: number) => Promise<PaginatedListResponse<TItem>>,
): Promise<PaginatedListResponse<TItem>> => {
  const firstPage = await fetchPage(FIRST_PAGE);
  if (firstPage.page_count <= FIRST_PAGE) {
    return firstPage;
  }

  const pendingPages: Promise<PaginatedListResponse<TItem>>[] = [];
  for (let page = FIRST_PAGE + 1; page <= firstPage.page_count; page++) {
    pendingPages.push(fetchPage(page));
  }

  const items = [...firstPage.data];
  for (const nextPage of await Promise.all(pendingPages)) {
    items.push(...nextPage.data);
  }

  return {
    page: FIRST_PAGE,
    per_page: items.length,
    page_count: FIRST_PAGE,
    data: items,
  };
};
